import { Component, OnDestroy, OnInit } from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {BehaviorSubject, of, Subject} from 'rxjs';
import { mergeMap, takeUntil, take } from 'rxjs/operators';
import { BackstrapService } from '../backstrap.service';
import { ContentService } from '../shared/content.service';
import { IUrlInfo } from '../contracts/content/iurl-info';
import { tap } from 'rxjs/internal/operators/tap';
import { switchMap } from 'rxjs/internal/operators/switchMap';
import {PhotocaptureService} from '../shared/photocapture.service';

@Component({
  selector: 'app-trifold-view',
  templateUrl: './trifold-view.component.html',
  styleUrls: ['./trifold-view.component.css']
})

export class TrifoldViewComponent implements OnInit, OnDestroy {
  // Subject to help unsubscribe from any active subscriptions on component destroy.
  private unsubscribe: Subject<any> = new Subject<any>();
  isLoading: boolean = true;
  printData: any = {};;
  repeatIndex: number = 0;;
  originalReportData: any = {};
  reportData: any = {};
  lastUpdate: any;
  formGroups: any[];
  crashId: string;
  reportUpdate = new Subject();
  pcrReport: any;
  schema: any;
  serviceLocationId: number;

  constructor(private route: ActivatedRoute,
              private api: BackstrapService,
              private contentService: ContentService,
              private router: Router,
              private photocaptureService: PhotocaptureService,
              ) {}

  ngOnInit() {
    let that = this;
    // Subscribe to the data on the route from the trifold-view resolver.
    this.route.data.pipe(
      takeUntil(this.unsubscribe),
      mergeMap( (response: { report: any }) => {
        // Set the report response.
        const report = response.report;
        // If the report exists...
        if (report) {
          that.reportData = JSON.parse(JSON.stringify(report));
          that.originalReportData = JSON.parse(JSON.stringify(that.reportData));
          that.crashId = that.reportData.crash_num;
          // SET THE LAST UPDATE TO NOW -- USED TO CHECK FOR DIRTY DATA LATER
          that.lastUpdate = new Date();
          var sv = null;
          if (that.reportData.rev_num) {
            sv = that.reportData.rev_num;
          }

          this.serviceLocationId = that.reportData['service_location_id'];

          let d;
          if(report.fnol_incident_datetime != null && report.fnol_incident_datetime !== '') {
            d = new Date(report.fnol_incident_datetime);
          }
          
          // GENERAL INFO
          that.printData['crash_date'] = {
            fieldName: 'crash_date',
            value: d != null ? [[that.getDate(d)]] : [['']],
            label: 'Date of Accident',
            type: 'text'
          };
          that.printData['crash_time'] = {
            fieldName: 'crash_time',
            value: d != null ? [[that.getTime(d)]] : [['']],
            label: 'Time of Call',
            type: 'text'
          };
          that.printData['location'] = {
            fieldName: 'location',
            value: report.fnol_incident_location !== '' ? [[report.fnol_incident_location]] : [['']],
            label: 'Reported Location',
            type: 'text'
          };

          switch(this.serviceLocationId) {
            case 1:
            case 3:
              this.reportData['nopd_item_number'] = {
                fieldName: 'item_number',
                value: report['item_number'] !== '' ? [[report['item_number']]] : [['']],
                label: 'NOPD Item Number',
                type: 'text'
              };
              break;
            case 2:
              this.reportData['bpd_item_number'] = {
                fieldName: 'item_number',
                value: report['item_number'] !== '' ? [[report['item_number']]] : [['']],
                label: 'BPD Item Number',
                type: 'text'
              };
              break;
            case 4:
              this.reportData['sgpd_item_number'] = {
                fieldName: 'item_number',
                value: report['item_number'] !== '' ? [[report['item_number']]] : [['']],
                label: 'SGPD Item Number',
                type: 'text'
              };
              break;
            default:
              this.reportData['nopd_item_number'] = {
                fieldName: 'item_number',
                value: report['item_number'] !== '' ? [[report['item_number']]] : [['']],
                label: 'NOPD Item Number',
                type: 'text'
              };
          }
          
          that.printData.vehicles = [];
          for(let v of report.vehicles) {
            let printVeh = [
              {
                fieldName: 'dr_lic_num',
                value: v.drivers_license_number !== '' ? [v.drivers_license_number] : [['']],
                label: 'License',
                type: 'text'
              },
             {
                fieldName: 'driver_address',
                value: [that.getAddress(v.driver_address_line_1, v.driver_address_line_2, v.driver_city, v.driver_state, v.driver_zip, v.driver_country)],
                label: 'Driver\'s Address',
                type: 'text'
              },
              {
                fieldNmae: 'driver_phone',
                value: v.driver_phone_number != null ? [v.driver_phone_number] : [''],
                label: 'Driver\'s Phone',
                type: 'text'
              },
              {
                fieldNmae: 'owner_name',
                value: [`${v.vehicle_owner_first_name} ${v.vehicle_owner_middle_name} ${v.vehicle_owner_last_name}`] || [['']],
                label: 'Owner\'s Name',
                type: 'text'
              },
              {
                fieldName: 'owner_address',
                value: [that.getAddress(v.vehicle_owner_address_line_1, v.vehicle_owner_address_line_2, v.vehicle_owner_city, v.vehicle_owner_state, v.vehicle_owner_zip, v.vehicle_owner_country)],
                label: 'Owner\'s Address',
                type: 'text'
              },
              {
                fieldNmae: 'insurance_company',
                value: v.insurance_company_name ? [v.insurance_company_name] : [''],
                label: 'Agency',
                type: 'text'
              },
              {
                fieldNmae: 'insurance_policy',
                value: v.insurance_policy_number ? [v.insurance_policy_number] : [''],
                label: 'Policy Number',
                type: 'text'
              },
              {
                fieldNmae: 'vehicle_year',
                value: v.vehicle_year ? [v.vehicle_year] : [''],
                label: 'Vehicle Year',
                type: 'text'
              },
              {
                fieldNmae: 'vehicle_make',
                value: v.vehicle_make ? [v.vehicle_make] : [''],
                label: 'Vehicle Make',
                type: 'text'
              },
              {
                fieldNmae: 'vehicle_model',
                value: v.vehicle_model ? [v.vehicle_model] : [''],
                label: 'Vehicle Model',
                type: 'text'
              },
            ]
            that.printData.vehicles.push(printVeh);
          }

          //reformat vehicles to make the template not a nightmare
          var vehicles = [];
          var subarrays = -1;
          for(var v = 0; v < that.printData.vehicles.length; v++){
            if((v + 1) % 2 === 1){
              subarrays++;
              if(vehicles[subarrays] === undefined) vehicles[subarrays] = [];
            }
            vehicles[subarrays].push(that.printData.vehicles[v]);
          }

          that.printData.vehicles = vehicles;
        } 
          
        that.populateForm();

        const reportId = report ? report.item_number : undefined;
        if (reportId) {
          return that.api.getPCRReport(reportId);
        } else {
          console.log('no report found with that id');
          return of(null);
        }
      }),
      tap((pcrReport) => {
        if (pcrReport && Object.keys(pcrReport).length) {
          that.pcrReport = pcrReport;
        }
      })
    ).subscribe();

    // Only send the admin an email or show print if the page is trifold or insDriver.
    this.contentService.urlInfo$.pipe(take(1))
    .subscribe((urlInfo: IUrlInfo) => {
      if (urlInfo.currentUrl.endsWith('/trifold') || urlInfo.currentUrl.endsWith('/insDriver')) {
        // SEND AN EMAIL TO ADMINS WITH DRIVER/INSURANCE INFO
        this.api.sendNotificationToAdmins(this.crashId).subscribe();
        setTimeout(() => {window.print()}, 500);
      } else {
        // Scroll to the top of the window when the page loads on the initial incident report page.
        const top: Element = document.getElementById('topOfInitialReportContent');
        if (top) {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }
    });
  }

  isCarrierDashboard(router: Router): boolean {
    return this.router.url.endsWith('/initialIncidentReport');
  }

  applyPhotoCaptureData() {
    this.reportData = this.photocaptureService.mergePCRtoCrashReport(this.pcrReport, this.originalReportData);
    delete this.pcrReport;
    this.reportUpdate.next(this.reportData);
  }

  populateForm(){
  	this.isLoading = false;
  }

  formatOptions(o) {
    o.value = [[o.value]];
    return o;
  }

  getAddress(l1, l2, city, state, zip, country) {
    let address = '';
    if (l1) {
      address += l1;
    }
    if (l2) {
      address += `\n${l2}`;
    }
    if (city || state || zip) {
      address += `\n${city || ''}${city && (state || zip) ? ', ' : ''}${state || ''}${state && zip ? ' ' : ''}${zip || ''}`;
    }
    if (country) {
      address += `\n${country}`;
    }
    return address;
  }

  getDate(dt) {
    const day = dt.getDate();
    const month = dt.getMonth() + 1; // Months are zero-based in JavaScript
    const year = dt.getFullYear();
    return `${month < 10 ? '0'+month : month}/${day < 10 ? '0'+day : day}/${year}`;
  }

  getTime(dt) {
    const hours = dt.getHours();
    const minutes = dt.getMinutes();
    return `${hours < 10 ? '0'+hours : hours}:${minutes < 10 ? '0'+minutes : minutes}`;
  }

  ngOnDestroy() {
    // Unsubscribe from any subscriptions.
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

}
